import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type TaskStatus = "PENDIENTE" | "EN_PROCESO" | "EN_REVISION" | "BLOQUEADA" | "APROBADA" | "FINALIZADA" | "CANCELADA";
export type TaskPriority = "BAJA" | "MEDIA" | "ALTA" | "URGENTE";

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En proceso",
  EN_REVISION: "En revisión",
  BLOQUEADA: "Bloqueada",
  APROBADA: "Aprobada",
  FINALIZADA: "Finalizada",
  CANCELADA: "Cancelada",
};

export const TASK_PRIORITY_LABEL: Record<TaskPriority, string> = {
  BAJA: "Baja",
  MEDIA: "Media",
  ALTA: "Alta",
  URGENTE: "Urgente",
};

export interface TaskRecord {
  id: string;
  clientId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  completedAt: string | null;
  tags: string[];
  createdAt: string;
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  assignedTo?: { id: string; fullName: string; email: string } | null;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface ListTasksFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedToUserId?: string;
  search?: string;
  overdue?: boolean;
}

export function useTasks(filters: ListTasksFilters = {}) {
  return useQuery({
    queryKey: ["tasks", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<TaskRecord[]>>("/tasks", { params: filters });
      return data;
    },
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ["tasks", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<TaskRecord>>(`/tasks/${id}`);
      return data.data;
    },
  });
}

export interface CreateTaskPayload {
  clientId?: string;
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string;
  tags?: string[];
  assignedToUserId?: string;
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTaskPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<TaskRecord>>("/tasks", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateTaskPayload> & { id: string; status?: TaskStatus }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<TaskRecord>>(`/tasks/${id}`, payload);
      return data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks", variables.id] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/tasks/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function taskClientName(task: TaskRecord): string | null {
  if (!task.client) return null;
  return task.client.clientType === "PERSONA_JURIDICA"
    ? task.client.businessName || task.client.tradeName || "Cliente"
    : task.client.fullName || "Cliente";
}

export function isOverdue(task: TaskRecord): boolean {
  if (!task.dueDate) return false;
  if (task.status === "FINALIZADA" || task.status === "CANCELADA") return false;
  return new Date(task.dueDate) < new Date(new Date().toDateString());
}
